import {
  DEFAULT_LOCATION,
  DEFAULT_LOCATION_COORDS,
  hasValidSavedCoordinates,
} from 'config/defaultLocation.config';
import Cookies from 'js-cookie';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUserSelectedLocation } from 'store/selectors/User/userSelectors';
import normalizePolygon from 'utils/maps/geometry.utils';
import { useGetJurisdictionsQuery } from '../services/geography/geography.api';
import {
  buildJurisdictionPolygons,
  getJurisdictionState,
  getNormalizedJurisdictionPolygons,
} from './utils/location.utils';

import type { UserLocation } from 'store/slices/User/user.types';

export type Jurisdiction = {
  GEOID: string;
  name: string;
  workplace: number;
  state?: string;
  state_long?: string;
  state_name?: string;
  original_coordinates: string; // JSON string of [longitude, latitude] pairs (GeoJSON order)
};

type LatLng = [number, number];
type Polygon = LatLng[]; // single ring
type Bounds = [LatLng, LatLng];

export type ProcessedJurisdiction = Jurisdiction & {
  polygon: Polygon | null;
  center: LatLng | null;
  bounds: Bounds | null;
};

// User-selected location shape (from selector) is declared in the user slice types
export type EffectiveLocationSource = 'user' | 'jurisdiction' | 'default';

export type EffectiveLocation = {
  placeId: string;
  name: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  coords: LatLng;
  bounds: Bounds | null;
  isLoading: boolean;
  source: EffectiveLocationSource;
};

export type JurisdictionObject = {
  jurisdictions: ProcessedJurisdiction[];
  homeJurisdiction: ProcessedJurisdiction | null;
  jurisdictionPolygons: Polygon[];
  locations: {
    /** @deprecated Prefer weatherLocation or workplaceLocation so location intent is explicit. */
    effectiveLocation: EffectiveLocation;
    weatherLocation: EffectiveLocation;
    workplaceLocation: EffectiveLocation;
    userLocation: UserLocation | null;
  };
  isLoading: boolean;
};

export const selectHomeJurisdiction = <T extends Jurisdiction>(
  jurisdictions: T[] = [],
  homeWorkplaceId?: number,
): T | null => {
  if (!Array.isArray(jurisdictions) || jurisdictions.length === 0) return null;

  const home =
    homeWorkplaceId == null || Number.isNaN(homeWorkplaceId)
      ? undefined
      : jurisdictions.find(
          (jurisdiction) => Number(jurisdiction?.workplace) === Number(homeWorkplaceId),
        );

  return home ?? jurisdictions[0];
};

// useLocation: fetch jurisdictions, compute processed jurisdictions and effective location
export const useLocation = (): JurisdictionObject => {
  const { data: rawData, isLoading: jurisdictionsLoading } = useGetJurisdictionsQuery(undefined);
  const jurisdictionsRaw = useMemo(() => (rawData ?? []) as Jurisdiction[], [rawData]);
  const workplaceIdFromState: number | undefined = useSelector(
    (state: any) => state.user?.workplace?.id,
  );
  const savedWorkplaceId = Cookies.get('workplaceId');
  const homeWorkplaceId =
    workplaceIdFromState ?? (savedWorkplaceId ? Number(savedWorkplaceId) : undefined);
  const userLocation = useSelector(selectUserSelectedLocation) as UserLocation | null;

  const { jurisdictions, homeJurisdiction, jurisdictionPolygons } = useMemo(() => {
    // Single-pass parse per jurisdiction to avoid repeated JSON.parse calls
    const processed: ProcessedJurisdiction[] = jurisdictionsRaw.map((jurisdiction) => {
      let polygon: Polygon | null = null;
      let center: LatLng | null = null;
      let bounds: Bounds | null = null;

      try {
        polygon = buildJurisdictionPolygons(jurisdiction);
        if (polygon && polygon.length) {
          const normalized = normalizePolygon(polygon);
          center = normalized?.center ?? null;
          bounds = normalized?.bounds ?? null;
        }
      } catch (error) {
        // ignore parse errors and keep nulls
      }

      return {
        ...jurisdiction,
        polygon,
        center,
        bounds,
      } as ProcessedJurisdiction;
    });

    const home = selectHomeJurisdiction(processed, homeWorkplaceId) as ProcessedJurisdiction | null;
    const normalized = getNormalizedJurisdictionPolygons(processed as any);

    return {
      jurisdictions: processed,
      homeJurisdiction: home,
      jurisdictionPolygons: normalized,
    };
  }, [jurisdictionsRaw, homeWorkplaceId]);

  const weatherLocation = useMemo<EffectiveLocation>(() => {
    const { placeId, source, city, state, latitude, longitude } = userLocation ?? {};

    const hasValidPlaceCoordinates = hasValidSavedCoordinates(latitude ?? null, longitude ?? null);

    if (hasValidPlaceCoordinates) {
      return {
        placeId: placeId ?? DEFAULT_LOCATION.place_id,
        name: city ?? DEFAULT_LOCATION.title,
        city: city ?? DEFAULT_LOCATION.city,
        state: state ?? DEFAULT_LOCATION.state_long,
        latitude: latitude!,
        longitude: longitude!,
        coords: [latitude!, longitude!],
        bounds: null,
        isLoading: false,
        source: source === 'user' ? 'user' : 'default',
      };
    }

    return {
      placeId: DEFAULT_LOCATION.place_id,
      name: DEFAULT_LOCATION.title,
      city: DEFAULT_LOCATION.city,
      state: DEFAULT_LOCATION.state_long,
      latitude: DEFAULT_LOCATION.latitude,
      longitude: DEFAULT_LOCATION.longitude,
      coords: DEFAULT_LOCATION_COORDS as LatLng,
      bounds: null,
      isLoading: userLocation?.status === 'checking',
      source: 'default',
    };
  }, [userLocation]);

  const workplaceLocation = useMemo<EffectiveLocation>(() => {
    const jurisdictionCenter = homeJurisdiction?.center;
    if (jurisdictionCenter) {
      return {
        placeId: DEFAULT_LOCATION.place_id,
        name: homeJurisdiction?.name ?? DEFAULT_LOCATION.title,
        city: homeJurisdiction?.name ?? DEFAULT_LOCATION.city,
        state: getJurisdictionState(homeJurisdiction),
        latitude: jurisdictionCenter[0],
        longitude: jurisdictionCenter[1],
        coords: jurisdictionCenter,
        bounds: homeJurisdiction?.bounds ?? null,
        isLoading: false,
        source: 'jurisdiction',
      };
    }

    return {
      placeId: DEFAULT_LOCATION.place_id,
      name: DEFAULT_LOCATION.title,
      city: DEFAULT_LOCATION.city,
      state: DEFAULT_LOCATION.state_long,
      latitude: DEFAULT_LOCATION.latitude,
      longitude: DEFAULT_LOCATION.longitude,
      coords: DEFAULT_LOCATION_COORDS as LatLng,
      bounds: null,
      isLoading: jurisdictionsLoading,
      source: 'default',
    };
  }, [homeJurisdiction, jurisdictionsLoading]);

  const isLoading = Boolean(jurisdictionsLoading) || userLocation?.status === 'checking';

  return useMemo(
    () => ({
      jurisdictions,
      homeJurisdiction,
      jurisdictionPolygons,
      locations: {
        effectiveLocation: weatherLocation,
        weatherLocation,
        workplaceLocation,
        userLocation: userLocation ?? null,
      },
      isLoading,
    }),
    [
      jurisdictions,
      homeJurisdiction,
      jurisdictionPolygons,
      weatherLocation,
      workplaceLocation,
      userLocation,
      isLoading,
    ],
  );
};

export default useLocation;
